/**
 * Created by neo on 10.01.2026
 * Community Tab - Displays community wall posts in a timeline layout
 */
import * as React from 'react';
import { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { CommunityWallPost, REACTION_TYPES } from '../../../Model/Engagement/CommunityWall/CommunityWallPost';
import { usePostReactions } from '../../../Hooks/usePostReactions';
import { Button } from '../../../Components/Button';
import { CreatePostModal } from './CreatePostModal';
import { Theme } from '../../../Theme/Theme';
import { Media, getMediaMedium, getMediaLarge } from '../../../Model/Media/Media';

export type CommunityTabProps = {
  posts: CommunityWallPost[];
  challengeId?: string;
  onPostsRefresh?: () => void;
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Timeline = styled.div`
  position: relative;
  max-width: 680px;
  margin: 0 auto;
  padding-left: 32px;

  &::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.lighterGrey} 100%);
    border-radius: 1px;
  }

  @media (max-width: 480px) {
    padding-left: 24px;

    &::before {
      left: 7px;
    }
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.3s ease-out forwards;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineDot = styled.div<{ hasMedia?: boolean }>`
  position: absolute;
  left: -32px;
  top: 16px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${(props) => (props.hasMedia ? Theme.Colors.primary : 'white')};
  border: 3px solid ${Theme.Colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  z-index: 1;

  @media (max-width: 480px) {
    left: -24px;
    width: 18px;
    height: 18px;
    border-width: 2px;
    top: 14px;
  }
`;

const PostCard = styled.div<{ isCompact?: boolean }>`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

const PostContent = styled.div<{ isCompact?: boolean }>`
  padding: ${(props) => (props.isCompact ? '12px 14px' : '16px')};
`;

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PostAvatar = styled.div<{ imageUrl?: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(props) => (props.imageUrl ? `url(${props.imageUrl})` : Theme.Colors.primary)};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
`;

const PostAuthor = styled.div`
  flex: 1;
  min-width: 0;
`;

const PostAuthorName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
`;

const PostLocation = styled.span`
  color: ${Theme.Colors.primary};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const PostText = styled.p<{ isCompact?: boolean }>`
  font-size: ${(props) => (props.isCompact ? '14px' : '15px')};
  color: ${Theme.Colors.textColor};
  margin: ${(props) => (props.isCompact ? '8px 0 0 0' : '12px 0 0 0')};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const MediaGrid = styled.div<{ count: number }>`
  display: grid;
  gap: 4px;
  margin-top: 12px;
  border-radius: 12px;
  overflow: hidden;
  grid-template-columns: ${(props) => (props.count === 1 ? '1fr' : 'repeat(2, 1fr)')};
  grid-template-rows: ${(props) => (props.count > 2 ? 'repeat(2, 1fr)' : '1fr')};
`;

const MediaThumbnail = styled.div<{ isLarge?: boolean }>`
  position: relative;
  aspect-ratio: ${(props) => (props.isLarge ? '16/9' : '1')};
  background: ${Theme.Colors.lightestGrey};
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:hover img,
  &:hover video {
    transform: scale(1.05);
  }

  &:hover .video-play-overlay {
    background: rgba(0, 0, 0, 0.35);
  }

  &:hover .play-button {
    transform: scale(1.1);
  }

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
  }

  &:first-of-type {
    grid-column: ${(props) => (props.isLarge ? '1 / -1' : 'auto')};
  }
`

const VideoPlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  transition: background 0.2s;
`;

const PlayButton = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
`;

const MoreMediaOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 20px;
  font-weight: 700;
`;

const PostReactions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid ${Theme.Colors.lightestGrey};
`;

const ReactionButtons = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const ReactionButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 5px 10px;
  border-radius: 14px;
  border: none;
  background: ${(props) => (props.isActive ? 'rgba(0, 221, 163, 0.15)' : Theme.Colors.lightestGrey)};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.isActive ? 'rgba(0, 221, 163, 0.25)' : Theme.Colors.lighterGrey)};
    transform: scale(1.05);
  }
`;

const ReactionEmoji = styled.span`
  font-size: 14px;
`;

const ReactionCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const TotalReactions = styled.span`
  font-size: 11px;
  color: ${Theme.Colors.warmGrey};
  white-space: nowrap;
`;

const EmptyPosts = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Theme.Colors.warmGrey};
  font-size: 15px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  max-width: 480px;
  margin: 0 auto;
`;

const CreatePostContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease-out;
`;

const LightboxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  color: white;
`;

const LightboxTitle = styled.div`
  font-size: 14px;
  opacity: 0.8;
`;

const LightboxClose = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const LightboxContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
`;

const LightboxMedia = styled.div`
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 8px;
  }

  video {
    max-width: 100%;
    max-height: 80vh;
    border-radius: 8px;
  }
`;

const LightboxNav = styled.button<{ direction: 'prev' | 'next' }>`
  position: absolute;
  ${(props) => (props.direction === 'prev' ? 'left: 20px;' : 'right: 20px;')}
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 600px) {
    width: 36px;
    height: 36px;
    font-size: 18px;
    ${(props) => (props.direction === 'prev' ? 'left: 8px;' : 'right: 8px;')}
  }
`;

const LightboxCounter = styled.div`
  text-align: center;
  padding: 12px;
  color: white;
  font-size: 13px;
  opacity: 0.7;
`;

type LightboxState = {
  isOpen: boolean;
  medias: Media[];
  currentIndex: number;
  authorName?: string;
};

type CommunityPostCardProps = {
  post: CommunityWallPost;
  onMediaClick: (medias: Media[], index: number, authorName?: string) => void;
};

const CommunityPostCard: React.FC<CommunityPostCardProps> = observer(({ post, onMediaClick }) => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { reactionsByType, myReaction, reactionCount, toggleReaction } = usePostReactions(post);

  const handleReaction = useCallback(
    (reactionType: string) => {
      toggleReaction(reactionType);
    },
    [toggleReaction],
  );

  const isCompact = !post.hasMedia;
  const displayMedias = post.medias.slice(0, 4);
  const extraCount = post.medias.length - 4;

  return (
    <TimelineItem>
      <TimelineDot hasMedia={post.hasMedia}>
        {post.hasMedia ? '📷' : '💬'}
      </TimelineDot>
      <PostCard isCompact={isCompact}>
        <PostContent isCompact={isCompact}>
          <PostHeader>
            <PostAvatar imageUrl={post.authorProfileImageUri}>
              {!post.authorProfileImage && (post.authorUsername?.charAt(0) || '?').toUpperCase()}
            </PostAvatar>
            <PostAuthor>
              <PostAuthorName>
                {post.authorUsername || t('motivation.anonymous', { defaultValue: 'Anonymous' })}
              </PostAuthorName>
              <PostMeta>
                <span>{dayjs(post.createdAt).fromNow()}</span>
                {post.locationId && (
                  <>
                    <span>·</span>
                    <PostLocation
                      onClick={() => navigate(`/motivation/challenge/${post.challengeId}/location/${post.locationId}`)}
                    >
                      📍 {t('motivation.atLocation', { defaultValue: 'at location' })}
                    </PostLocation>
                  </>
                )}
              </PostMeta>
            </PostAuthor>
          </PostHeader>

          {post.text && <PostText isCompact={isCompact}>{post.text}</PostText>}

          {post.hasMedia && (
            <MediaGrid count={displayMedias.length}>
              {displayMedias.map((media, idx) => {
                const isVideo = media.mediaType?.startsWith('video');
                const isLarge = displayMedias.length === 1 || (displayMedias.length === 3 && idx === 0);
                const isLastWithMore = idx === 3 && extraCount > 0;

                return (
                  <MediaThumbnail
                    key={media.id || idx}
                    isLarge={isLarge}
                    onClick={() => onMediaClick(post.medias, idx, post.authorUsername)}
                  >
                    {isVideo ? (
                      <>
                        <video src={media.url} poster={getMediaMedium(media)} muted />
                        <VideoPlayOverlay className="video-play-overlay">
                          <PlayButton className="play-button">▶</PlayButton>
                        </VideoPlayOverlay>
                      </>
                    ) : (
                      <img src={getMediaMedium(media)} alt="" />
                    )}
                    {isLastWithMore && <MoreMediaOverlay>+{extraCount}</MoreMediaOverlay>}
                  </MediaThumbnail>
                );
              })}
            </MediaGrid>
          )}

          <PostReactions>
            <ReactionButtons>
              {Object.entries(REACTION_TYPES).map(([key, emoji]) => {
                const count = reactionsByType[emoji] ?? 0;
                const isActive = myReaction === emoji;
                return (
                  <ReactionButton key={key} isActive={isActive} onClick={() => handleReaction(emoji)}>
                    <ReactionEmoji>{emoji}</ReactionEmoji>
                    {count > 0 && <ReactionCount>{count}</ReactionCount>}
                  </ReactionButton>
                );
              })}
            </ReactionButtons>
            {reactionCount > 0 && (
              <TotalReactions>
                {t('motivation.reactions', { defaultValue: '{{count}} reactions', count: reactionCount })}
              </TotalReactions>
            )}
          </PostReactions>
        </PostContent>
      </PostCard>
    </TimelineItem>
  );
});

export const CommunityTab: React.FC<CommunityTabProps> = observer(({ posts, challengeId, onPostsRefresh }) => {
  const { t } = useTrans();
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false);
  const [lightbox, setLightbox] = useState<LightboxState>({
    isOpen: false,
    medias: [],
    currentIndex: 0,
  });

  const handlePostCreated = useCallback(() => {
    if (onPostsRefresh) {
      onPostsRefresh();
    }
  }, [onPostsRefresh]);

  const openLightbox = useCallback((medias: Media[], index: number, authorName?: string) => {
    setLightbox({ isOpen: true, medias, currentIndex: index, authorName });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const navigateLightbox = useCallback((direction: 'prev' | 'next') => {
    setLightbox((prev) => {
      const newIndex =
        direction === 'prev'
          ? (prev.currentIndex - 1 + prev.medias.length) % prev.medias.length
          : (prev.currentIndex + 1) % prev.medias.length;
      return { ...prev, currentIndex: newIndex };
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    },
    [closeLightbox, navigateLightbox],
  );

  const currentMedia = lightbox.medias[lightbox.currentIndex];
  const isCurrentVideo = currentMedia?.mediaType?.startsWith('video');

  return (
    <>
      {challengeId && (
        <CreatePostContainer>
          <Button color="primary" onClick={() => setCreatePostModalOpen(true)}>
            {t('community.createPost', { defaultValue: 'Create Post' })}
          </Button>
        </CreatePostContainer>
      )}

      {posts.length === 0 ? (
        <EmptyPosts>
          {t('motivation.noPosts', {
            defaultValue: 'No community posts yet. Join a challenge and share your experience!',
          })}
        </EmptyPosts>
      ) : (
        <Timeline>
          {posts.map((post) => (
            <CommunityPostCard key={post.id} post={post} onMediaClick={openLightbox} />
          ))}
        </Timeline>
      )}

      {challengeId && (
        <CreatePostModal
          isOpen={createPostModalOpen}
          onClose={() => setCreatePostModalOpen(false)}
          challengeId={challengeId}
          onPostCreated={handlePostCreated}
        />
      )}

      {lightbox.isOpen && currentMedia && (
        <LightboxOverlay onClick={closeLightbox} onKeyDown={handleKeyDown} tabIndex={0}>
          <LightboxHeader onClick={(e) => e.stopPropagation()}>
            <LightboxTitle>
              {lightbox.authorName || t('motivation.anonymous', { defaultValue: 'Anonymous' })}
            </LightboxTitle>
            <LightboxClose onClick={closeLightbox}>✕</LightboxClose>
          </LightboxHeader>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            {lightbox.medias.length > 1 && (
              <LightboxNav direction="prev" onClick={() => navigateLightbox('prev')}>
                ‹
              </LightboxNav>
            )}
            <LightboxMedia>
              {isCurrentVideo ? (
                <video
                  key={currentMedia.id}
                  src={currentMedia.url || getMediaLarge(currentMedia)}
                  controls
                  autoPlay
                />
              ) : (
                <img
                  key={currentMedia.id}
                  src={getMediaLarge(currentMedia)}
                  alt=""
                />
              )}
            </LightboxMedia>
            {lightbox.medias.length > 1 && (
              <LightboxNav direction="next" onClick={() => navigateLightbox('next')}>
                ›
              </LightboxNav>
            )}
          </LightboxContent>
          {lightbox.medias.length > 1 && (
            <LightboxCounter>
              {lightbox.currentIndex + 1} / {lightbox.medias.length}
            </LightboxCounter>
          )}
        </LightboxOverlay>
      )}
    </>
  );
});
